/**
 * Complex-entity grouping — the pass that folds a DXF entity's sibling
 * child records onto their owner.
 *
 * Two AutoCAD entities own a run of following records terminated by a
 * `SEQEND` (Autodesk DXF reference, "Complex entities"):
 *
 *   - `INSERT` with group 66 = 1 owns its `ATTRIB` run;
 *   - `POLYLINE` (the legacy heavy polyline) owns its `VERTEX` run.
 *
 * In the file those children are SIBLINGS of the owner, not nested records,
 * so every parse path has to re-associate them. Before this module the
 * INSERT half was implemented three separate times (the eager
 * `parseEntitiesSection`, the BLOCKS child walk in `sectionParsers.ts` and
 * the streaming `streamEntitiesUntilEndsec`); adding a second entity family
 * to each of those would have produced six near-identical loops. Both
 * families now share ONE fold here — one domain truth, called from all three
 * paths (CLAUDE.md Этап 1 `dup:check`: extract the shared helper rather than
 * baseline the clone).
 *
 * Two entry points exist because the callers have genuinely different shapes,
 * not because the rule differs: {@link foldComplexEntities} folds a
 * materialised array (eager parser, BLOCKS children) and
 * {@link foldComplexEntitiesStreaming} folds a peekable chunk stream (the
 * streaming parser, which must never materialise the section). They share
 * {@link mergeChildrenIntoOwner} so the merge semantics cannot drift apart.
 *
 * Source of truth: Autodesk DXF reference (INSERT/ATTRIB/SEQEND and
 * POLYLINE/VERTEX/SEQEND grouping) + ezdxf 1.4.4 `entities/polyline.py`
 * (`Polyline.vertices` / `seqend`). AutoCAD MCP is BANNED.
 */

import type {
  DxfAttribEntity,
  DxfEntity,
  DxfInsertEntity,
  DxfPolylineEntity,
  DxfSeqendEntity,
  DxfSupportedEntity,
  DxfVertexEntity,
} from "./types";

/** True when `entity` owns a child run that this pass must fold. */
export function ownsComplexChildren(entity: DxfEntity): boolean {
  if (entity.kind !== "supported-entity") return false;
  if (entity.type === "INSERT") {
    return entity.geometry.attributesFollow === true;
  }
  // A POLYLINE always owns a VERTEX run (group 66 is marked obsolete by the
  // DXF reference and some writers omit it), so it is never gated on 66.
  return entity.type === "POLYLINE";
}

/** The child entity type an owner accepts, or `null` for a non-owner. */
function childTypeFor(entity: DxfEntity): "ATTRIB" | "VERTEX" | null {
  if (entity.kind !== "supported-entity") return null;
  if (entity.type === "INSERT") return "ATTRIB";
  if (entity.type === "POLYLINE") return "VERTEX";
  return null;
}

/** Accumulated child records for one owner. */
type ComplexChildren = {
  attribs: DxfAttribEntity[];
  vertices: DxfVertexEntity[];
  seqend: DxfSeqendEntity | null;
};

function emptyChildren(): ComplexChildren {
  return { attribs: [], vertices: [], seqend: null };
}

/**
 * Classify one candidate child. Returns `"child"` when it was absorbed,
 * `"seqend"` when it terminated the run, and `"stop"` for anything else —
 * AutoCAD always writes the SEQEND, so `"stop"` is only a safety net for a
 * malformed file, and it must leave the entity in the sibling stream rather
 * than swallow it.
 */
function absorbChild(
  owner: DxfEntity,
  candidate: DxfEntity,
  out: ComplexChildren,
): "child" | "seqend" | "stop" {
  if (candidate.kind !== "supported-entity") return "stop";
  const wanted = childTypeFor(owner);
  if (wanted !== null && candidate.type === wanted) {
    if (candidate.type === "ATTRIB") out.attribs.push(candidate);
    else if (candidate.type === "VERTEX") out.vertices.push(candidate);
    return "child";
  }
  if (candidate.type === "SEQEND") {
    out.seqend = candidate;
    return "seqend";
  }
  return "stop";
}

/**
 * Produce the owner with its children merged in.
 *
 * INSERT keeps the SEQEND's HANDLE only (its long-standing shape — the
 * writer rebuilds the record from the parent's layer/space/owner). POLYLINE
 * keeps the WHOLE SEQEND so its own handle, owner AND layer round-trip
 * verbatim: those three were exactly what the previous raw-passthrough
 * workaround (`dxfBridge.seqendPassthrough.ts`) existed to stop losing.
 */
function mergeChildrenIntoOwner(
  owner: DxfSupportedEntity,
  children: ComplexChildren,
): DxfSupportedEntity {
  if (owner.type === "INSERT") {
    const merged: DxfInsertEntity = {
      ...owner,
      geometry: {
        ...owner.geometry,
        attribs: children.attribs,
        ...(children.seqend?.common.handle !== undefined
          ? { seqendHandle: children.seqend.common.handle }
          : {}),
      },
    };
    return merged;
  }
  if (owner.type === "POLYLINE") {
    const merged: DxfPolylineEntity = {
      ...owner,
      geometry: {
        ...owner.geometry,
        vertices: children.vertices,
        ...(children.seqend !== null ? { seqend: children.seqend } : {}),
      },
    };
    return merged;
  }
  return owner;
}

/**
 * Fold an already-materialised entity list. Non-owners and orphan children
 * (a stray ATTRIB / VERTEX / SEQEND with no preceding owner, which only a
 * malformed file produces) pass through untouched so nothing is ever
 * dropped.
 */
export function foldComplexEntities(entities: DxfEntity[]): DxfEntity[] {
  const out: DxfEntity[] = [];
  for (let i = 0; i < entities.length; i++) {
    const entity = entities[i]!;
    if (!ownsComplexChildren(entity) || entity.kind !== "supported-entity") {
      out.push(entity);
      continue;
    }
    const children = emptyChildren();
    let j = i + 1;
    while (j < entities.length) {
      const verdict = absorbChild(entity, entities[j]!, children);
      if (verdict === "stop") break;
      j++;
      if (verdict === "seqend") break;
    }
    out.push(mergeChildrenIntoOwner(entity, children));
    i = j - 1;
  }
  return out;
}

/** Minimal look-ahead cursor the streaming fold needs. */
export type ComplexChildCursor = {
  done: () => boolean;
  /** Peek the next entity WITHOUT consuming it, or `null` at the end. */
  peek: () => DxfEntity | null;
  /** Consume and discard the peeked entity (already returned by `peek`). */
  advance: () => void;
};

/**
 * Streaming counterpart of {@link foldComplexEntities}: pull children off
 * `cursor` until the run ends, then return the merged owner. The caller
 * keeps ownership of the stream, so nothing is materialised beyond the one
 * owner's own children — which the typed model has to hold anyway.
 */
export function foldComplexEntitiesStreaming(
  owner: DxfSupportedEntity,
  cursor: ComplexChildCursor,
): DxfSupportedEntity {
  const children = emptyChildren();
  while (!cursor.done()) {
    const peeked = cursor.peek();
    if (!peeked) break;
    const verdict = absorbChild(owner, peeked, children);
    if (verdict === "stop") break;
    cursor.advance();
    if (verdict === "seqend") break;
  }
  return mergeChildrenIntoOwner(owner, children);
}
